/**
 * DeckSprite - デッキ（山札）スプライト
 * 積み重なったカードの表示と残り枚数表示を担当
 */
import Phaser from 'phaser';
import { PHASER_CONFIG } from '../../utils/constants';

export class DeckSprite extends Phaser.GameObjects.Container {
  private cardImages: Phaser.GameObjects.Image[] = [];
  private countText!: Phaser.GameObjects.Text;
  private countBg!: Phaser.GameObjects.Graphics;
  private isInteractable: boolean = true;

  // 積み重ねカードの枚数
  private static readonly STACK_COUNT = 5;
  private static readonly STACK_OFFSET = 3;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);

    this.createStackedCards();
    this.createCountDisplay();
    this.setupInteractive();
  }

  /**
   * 積み重ねカードを作成
   */
  private createStackedCards(): void {
    for (let i = 0; i < DeckSprite.STACK_COUNT; i++) {
      const offsetX = i * DeckSprite.STACK_OFFSET;
      const offsetY = -i * DeckSprite.STACK_OFFSET;

      const cardImage = this.scene.add.image(offsetX, offsetY, 'card_back');
      cardImage.setDisplaySize(
        PHASER_CONFIG.CARD_SIZE.width,
        PHASER_CONFIG.CARD_SIZE.height
      );

      this.cardImages.push(cardImage);
      this.add(cardImage);
    }
  }

  /**
   * 残り枚数表示を作成
   */
  private createCountDisplay(): void {
    // 背景
    this.countBg = this.scene.add.graphics();
    this.countBg.fillStyle(0x000000, 0.7);
    this.countBg.fillRoundedRect(-25, 60, 50, 30, 8);
    this.add(this.countBg);

    // テキスト
    this.countText = this.scene.add.text(0, 75, '0', {
      fontFamily: 'Arial',
      fontSize: '18px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.countText.setOrigin(0.5);
    this.add(this.countText);
  }

  /**
   * インタラクティブ設定
   */
  private setupInteractive(): void {
    const hitArea = new Phaser.Geom.Rectangle(
      -PHASER_CONFIG.CARD_SIZE.width / 2,
      -PHASER_CONFIG.CARD_SIZE.height / 2,
      PHASER_CONFIG.CARD_SIZE.width,
      PHASER_CONFIG.CARD_SIZE.height
    );

    this.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
    this.setSize(PHASER_CONFIG.CARD_SIZE.width, PHASER_CONFIG.CARD_SIZE.height);

    // ホバーエフェクト
    this.on('pointerover', () => {
      if (this.isInteractable) {
        this.scene.tweens.add({
          targets: this,
          scaleX: 1.05,
          scaleY: 1.05,
          duration: 150,
          ease: 'Power2',
        });
      }
    });

    this.on('pointerout', () => {
      this.scene.tweens.add({
        targets: this,
        scaleX: 1,
        scaleY: 1,
        duration: 150,
        ease: 'Power2',
      });
    });

    // カーソル変更
    this.on('pointerover', () => {
      if (this.isInteractable) {
        this.scene.input.setDefaultCursor('pointer');
      }
    });

    this.on('pointerout', () => {
      this.scene.input.setDefaultCursor('default');
    });
  }

  /**
   * 残り枚数を更新
   */
  public updateCount(count: number): void {
    this.countText.setText(count.toString());

    // 枚数に応じて積み重ねの表示を調整
    const visibleStacks = Math.min(
      DeckSprite.STACK_COUNT,
      Math.ceil(count / 10)
    );

    this.cardImages.forEach((card, index) => {
      card.setVisible(index < visibleStacks);
    });

    // 0枚の場合は暗くする
    if (count === 0) {
      this.setAlpha(0.5);
      this.isInteractable = false;
    } else {
      this.setAlpha(1);
      this.isInteractable = true;
    }
  }

  /**
   * インタラクティブ状態を設定
   */
  public setInteractable(enabled: boolean): void {
    this.isInteractable = enabled;

    if (!enabled) {
      this.setAlpha(0.7);
    } else {
      this.setAlpha(1);
    }
  }

  /**
   * ドローアニメーション（カードが飛び出す演出）
   */
  public playDrawAnimation(): void {
    const topCard = this.cardImages[this.cardImages.length - 1];
    if (!topCard) return;

    // 一時的に動かす
    this.scene.tweens.add({
      targets: topCard,
      y: topCard.y - 30,
      duration: 100,
      yoyo: true,
      ease: 'Power2',
    });
  }
}
